/** @jsx jsx */
import React, { memo, useMemo, useCallback, type ReactElement } from 'react';
import { css, jsx } from '@compiled/react';
import Tooltip from '@atlaskit/tooltip';
import {
	useCollabStore,
	NodeSelectionWrapper,
	getPresenceNodeId,
	ParticipantBadge,
} from '@atlassian/jira-business-collaboration';
import type { BusinessIssueField } from '@atlassian/jira-business-entity-project';
import { useIntl } from '@atlassian/jira-intl';
import { ISSUE_TYPE } from '@atlassian/jira-platform-field-config';
import {
	DEFAULT_ROW_HEIGHT,
	EXPAND_ICON_INDENT_WIDTH,
	LIST_PADDING_LEFT,
} from '../../../../common/constants';
import type { ListViewControllerData } from '../../../../controllers/list-view-issues/types';
// eslint-disable-next-line jira/import/no-parent-imports
import { CellNavigation } from '../../cell-navigation';
import messages from './messages';

type Props = {
	issueId: string;
	fieldId: string;
	fieldType?: string;
	projectId: string;
	position: string;
	rowIndex: number;
	columnIndex: number;
	width: number;
	field?: BusinessIssueField;
	issue?: ListViewControllerData;
	depth?: number;
	children: () => ReactElement;
};

export const WithCellSelection = memo<Props>(
	({
		projectId,
		fieldId,
		issueId,
		position,
		children,
		rowIndex,
		columnIndex,
		width,
		field,
		issue,
		depth = 0,
	}: Props) => {
		const { formatMessage } = useIntl();
		const [{ nodeSelection }, { sendNodeSelected, sendClearNodeSelection }] = useCollabStore();

		const nodeId = useMemo(
			() => getPresenceNodeId({ projectId, viewId: 'list', nodeId: `${fieldId}-${issueId}` }),
			[projectId, issueId, fieldId],
		);

		const selections = nodeSelection[nodeId];
		const selection = selections && selections[0];

		const isIssueType = fieldId === ISSUE_TYPE;

		const onSendNodeSelected = useCallback(
			() => sendNodeSelected({ nodeId }),
			[sendNodeSelected, nodeId],
		);
		const onSendClearNodeSelection = useCallback(
			() => sendClearNodeSelection({ nodeId }),
			[sendClearNodeSelection, nodeId],
		);

		const renderedChildren = useMemo(
			() =>
				children && (
					<CellNavigation
						columnIndex={columnIndex}
						rowIndex={rowIndex}
						field={field}
						issue={issue}
						onSendNodeSelected={onSendNodeSelected}
						onSendClearNodeSelection={onSendClearNodeSelection}
					>
						{children()}
					</CellNavigation>
				),
			[children, columnIndex, onSendClearNodeSelection, onSendNodeSelected, rowIndex, field, issue],
		);

		if (selection) {
			const hasMoreSelections = selections && selections.length > 1;
			// exclude first selection of the list
			const tooltipContent = hasMoreSelections ? (
				<>{selections.map(({ user }, i) => i !== 0 && <div key={user.name}>{user.name}</div>)}</>
			) : null;
			const otherSelectionsContent = hasMoreSelections
				? formatMessage(messages.moreSelections, {
						count: selections.length - 1,
					})
				: '';

			return (
				<NodeWrapper isIssueType={isIssueType} width={width} depth={depth}>
					<NodeSelectionWrapper color={selection.color}>
						<Tooltip content={tooltipContent} position="top">
							<ParticipantBadge color={selection.color} position={position}>
								{selection.user.name}
								{otherSelectionsContent}
							</ParticipantBadge>
						</Tooltip>
						{renderedChildren}
					</NodeSelectionWrapper>
				</NodeWrapper>
			);
		}

		return renderedChildren;
	},
);

const nodeWrapperBaseStyles = css({
	position: 'absolute',
	width: 'inherit',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${DEFAULT_ROW_HEIGHT}px`,
});

const NodeWrapper = ({
	isIssueType,
	width,
	depth,
	children,
	...rest
}: {
	isIssueType: boolean;
	width: number;
	depth: number;
	children: React.ReactNode;
}) => (
	<div
		css={nodeWrapperBaseStyles}
		// eslint-disable-next-line jira/react/no-style-attribute
		style={{
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			...(isIssueType && {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				paddingLeft: `${depth > 0 ? EXPAND_ICON_INDENT_WIDTH * depth + 4 : 0}px`,
				boxSizing: 'border-box',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				width: `${width - LIST_PADDING_LEFT}px`,
			}),
		}}
		{...rest}
	>
		{children}
	</div>
);
